import React, { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface FormFieldProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    type?: string;
    id?: string;
    placeholder: string;
    autoComplete?: string;
    required?: boolean;
    error?: string;
    autoFocus?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
}

const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
    ({ value, onChange, type = "text", id, placeholder, autoComplete = "off", required = false, error, autoFocus = true, readOnly = false, disabled = false }, ref) => {
        const [hide, setHide] = useState(true);

        if (type === "password") {
            return (
                <div className="gs-auth-field-wrap">
                    <Input
                        ref={ref}
                        autoFocus={autoFocus}
                        value={value}
                        onChange={onChange}
                        type={hide ? "password" : "text"}
                        autoComplete={autoComplete}
                        required={required}
                        readOnly={readOnly}
                        disabled={disabled}
                        id={id}
                        placeholder={placeholder}
                        className="gs-auth-input-lg pr-10"
                    />
                    <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setHide(!hide)}
                        className="gs-auth-password-toggle"
                        aria-label={hide ? "Show password" : "Hide password"}
                    >
                        {hide ? (
                            <Eye id={`${id}-icon`} className="size-[1.125rem]" />
                        ) : (
                            <EyeOff id={`${id}-icon`} className="size-[1.125rem]" />
                        )}
                    </button>
                    {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
                </div>
            );
        }

        return (
            <div>
                <Input
                    ref={ref}
                    autoFocus={autoFocus && !readOnly && !disabled}
                    value={value}
                    onChange={onChange}
                    type={type}
                    autoComplete={autoComplete}
                    required={required}
                    readOnly={readOnly}
                    disabled={disabled}
                    tabIndex={readOnly || disabled ? -1 : undefined}
                    onFocus={readOnly ? (e) => e.currentTarget.blur() : undefined}
                    id={id}
                    placeholder={placeholder}
                    className={cn(
                        "h-11 text-sm",
                        readOnly &&
                            "cursor-default bg-muted/50 focus:!border-border focus:!shadow-none focus-visible:!border-border focus-visible:!shadow-none",
                        disabled && "cursor-default"
                    )}
                />
                {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
            </div>
        );
    }
);

FormField.displayName = "FormField";

export default FormField;
